import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ds";

const ProfileCompletion = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [age, setAge] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();
  const { user, updateUserProfile } = useAuth();

  //--> DS toast (replaces the removed MUI Snackbar). Bridge the legacy
  //    setSnackbarOpen(...) triggers so every existing call-site keeps working.
  const toast = useToast();
  useEffect(() => {
    if (snackbarOpen && snackbarMessage) {
      (toast[snackbarSeverity] || toast.info)(snackbarMessage);
      setSnackbarOpen(false);
    }
  }, [snackbarOpen, snackbarMessage, snackbarSeverity, toast]);

  useEffect(() => {
    // Populate fields with existing user data if available
    if (user) {
      if (user.first_name) setFirstName(user.first_name);
      if (user.last_name) setLastName(user.last_name);
      if (user.phone) setPhoneNumber(user.phone);
      if (user.address) setAddress(user.address);
    }

    // Show initial welcome message
    setMessage("Please complete your profile to continue using DeeTrade.");
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (age && (isNaN(age) || parseInt(age) < 18 || parseInt(age) > 120)) {
      newErrors.age = "Please enter a valid age between 18 and 120";
    }
    if (phoneNumber && !/^\+?[\d\s-]{10,}$/.test(phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Create user data with all collected fields
      const userData = {
        firstName,
        lastName,
        age: age ? parseInt(age) : null,
        phoneNumber,
        address,
      };

      // Structure the API request with both profile_data object
      // and first-level fields to ensure compatibility
      const profileData = {
        profile_data: userData,
        // Also include fields at top level to ensure they're captured
        // by the backend regardless of structure
        firstName,
        lastName,
        phoneNumber,
        address,
      };

      const response = await updateUserProfile(profileData);

      // Set success message and open snackbar
      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Set message state for existing UI elements
      setMessage("Profile updated successfully! Redirecting to dashboard...");

      // Redirect after a delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error("Profile update error:", error);
      const errorMsg =
        error.message || "Failed to update profile. Please try again.";

      // Set error message in snackbar
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

      setMessage(errorMsg);
      setErrors({ submit: error.message || "An unexpected error occurred" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="ds-h2 text-white mb-6 text-center">
          Complete Your Profile
        </h2>

        <div className="mb-4 text-center">
          <p className="text-fg-3 text-sm">
            Welcome to DeeTrade! Please provide your details to complete your
            profile setup.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-200 mb-1">First Name*</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="John"
            />
            {errors.firstName && (
              <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-200 mb-1">Last Name*</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="Doe"
            />
            {errors.lastName && (
              <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-200 mb-1">Age</label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="25"
            />
            {errors.age && (
              <p className="text-red-400 text-sm mt-1">{errors.age}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-200 mb-1">Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="+1234567890"
            />
            {errors.phoneNumber && (
              <p className="text-red-400 text-sm mt-1">{errors.phoneNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-200 mb-1">Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded bg-white/10 text-white"
              placeholder="Your address"
              rows="3"
            />
          </div>

          {message && (
            <div
              className={`p-3 my-4 rounded ${
                message.includes("successfully")
                  ? "bg-green-500/20 text-green-400"
                  : "bg-blue-500/20 text-blue-400"
              }`}
            >
              <p className="text-center font-medium">{message}</p>
            </div>
          )}

          {errors.submit && (
            <p className="text-red-400 text-sm text-center">{errors.submit}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Complete Profile"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileCompletion;
